<template>
  <div class="time-select">
    <div class="time-select__input">
      <v-text-field
        v-model="displayValue"
        :label="label"
        :rules="rules"
        :prepend-icon="prependIcon"
        @click:prepend="openDropdown"
        @click="openDropdown"
        @input="handleInput"
        @keydown.enter.prevent="formatInputTime"
        @focus="handleFocus"
        @blur="handleBlur"
        dense
        :hide-details="!showError"
        ref="input"
      ></v-text-field>
    </div>
    <div v-if="isOpen" class="time-select__dropdown" :class="{ 'theme--dark': isDark }" ref="dropdown">
      <div class="time-select__list" ref="list">
        <div
          v-for="(time, index) in times"
          :key="index"
          class="time-select__item"
          :class="{
            'time-select__item--selected': time === value,
            'time-select__item--matching': isMatchingTime(time),
            'theme--dark': isDark,
          }"
          @mousedown.prevent="selectTime(time)"
          ref="items"
        >
          {{ time }}
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { DateTime } from "luxon";
import { mapGetters } from "vuex";

export default {
  name: "TimeSelect",
  props: {
    value: {
      type: String,
      required: true,
    },
    label: {
      type: String,
      default: "",
    },
    rules: {
      type: Array,
      default: () => [],
    },
    prependIcon: {
      type: String,
      default: "mdi-clock-time-four-outline",
    },
    times: {
      type: Array,
      required: true,
    },
    matchValue: {
      type: String,
      default: null,
    },
  },
  data() {
    return {
      isOpen: false,
      displayValue: "",
      showError: false,
      rawInput: "",
    };
  },
  computed: {
    ...mapGetters("settings", ["settings"]),
    isDark() {
      return this.$vuetify.theme.dark;
    },
    is24Hour() {
      return this.settings?.timeFormat24Hour || false;
    },
  },
  methods: {
    formatTimeDisplay(time) {
      if (!time) return "";

      try {
        // If time is already a DateTime object, use it directly
        if (typeof time === "object" && time.isLuxonDateTime) {
          return this.is24Hour ? time.toFormat("HH:mm:ss") : time.toFormat("h:mm a");
        }

        // If time is a string in HH:mm:ss format and we're in 24-hour mode, return as is
        if (this.is24Hour && /^\d{2}:\d{2}:\d{2}$/.test(time)) {
          return time;
        }

        const dt = this.parseTimeInput(time);
        if (!dt) return time;
        return this.is24Hour ? dt.toFormat("HH:mm:ss") : dt.toFormat("h:mm a");
      } catch (error) {
        console.error("Error in formatTimeDisplay:", error);
        return time;
      }
    },

    parseTimeInput(value) {
      if (!value) return null;

      try {
        let hour = 0;
        let minute = 0;
        let meridiem = "";

        // Handle 24-hour format (HH:mm:ss)
        if (this.is24Hour && /^\d{2}:\d{2}:\d{2}$/.test(value)) {
          const [hours, minutes] = value.split(":");
          hour = parseInt(hours);
          minute = parseInt(minutes);
          if (hour >= 0 && hour <= 23 && minute >= 0 && minute < 60) {
            return DateTime.fromObject({ hour, minute, second: 0 }, { zone: this.settings?.timeZone || "local" });
          }
          return null;
        }

        // Handle direct selection from dropdown
        if (value.includes(" ")) {
          const [timePart, ampm] = value.split(" ");
          const [hours, minutes] = timePart.split(":");
          hour = parseInt(hours);
          minute = parseInt(minutes);
          meridiem = ampm.toUpperCase();
        } else {
          // Remove spaces and convert to uppercase
          let input = value.replace(/\s+/g, "").toUpperCase();

          // Extract AM/PM if present
          const meridiemMatch = input.match(/(AM|PM)$/i);
          if (meridiemMatch) {
            meridiem = meridiemMatch[0];
            input = input.slice(0, -meridiem.length);
          }

          // Parse time formats: 3-4 digits or separated by : or .
          if (/^\d{3,4}$/.test(input)) {
            hour = input.length === 3 ? parseInt(input[0]) : parseInt(input.slice(0, 2));
            minute = input.length === 3 ? parseInt(input.slice(1)) : parseInt(input.slice(2));
          } else {
            const [h, m] = input.split(/[.:]/);
            hour = parseInt(h);
            minute = parseInt(m || 0);
          }
        }

        // Validate parsed values
        if (isNaN(hour) || isNaN(minute) || minute >= 60) return null;

        // Convert to 24-hour format
        if (!this.is24Hour) {
          if (!meridiem) {
            // For end times (we have a matchValue to compare against)
            if (this.matchValue) {
              const startTime = this.parseTimeInput(this.matchValue);
              if (startTime) {
                // Convert input to 24-hour for comparison
                let compareHour = hour;
                if (hour === 12) compareHour = 0;
                if (compareHour < startTime.hour || (compareHour === startTime.hour && minute < startTime.minute)) {
                  // If time would be before start time, make it PM
                  meridiem = "PM";
                  hour = hour === 12 ? 12 : hour;
                } else {
                  // If time would be after start time, keep it in the same period as start time
                  meridiem = startTime.hour >= 12 ? "PM" : "AM";
                  hour = hour === 0 ? 12 : hour > 12 ? hour % 12 || 12 : hour;
                }
              }
            } else {
              // For start times or times without comparison
              meridiem = hour === 0 ? "AM" : hour === 12 ? "PM" : hour > 12 ? "PM" : "AM";
              hour = hour === 0 ? 12 : hour > 12 ? hour % 12 || 12 : hour;
            }
          }

          // Final conversion to 24-hour format
          hour = meridiem === "PM" && hour !== 12 ? hour + 12 : meridiem === "AM" && hour === 12 ? 0 : hour;
        } else if (hour > 23) {
          return null;
        }

        return DateTime.fromObject({ hour, minute, second: 0 }, { zone: this.settings?.timeZone || "local" });
      } catch (error) {
        console.error("Error in parseTimeInput:", error);
        return null;
      }
    },

    scrollToSelected() {
      this.$nextTick(() => {
        const listEl = this.$refs.list;
        if (!listEl || !this.value) return;

        const currentTime = this.parseTimeToMinutes(this.value);
        let closestIndex = 0;
        let smallestDiff = Infinity;

        this.times.forEach((time, index) => {
          const diff = Math.abs(this.parseTimeToMinutes(time) - currentTime);
          if (diff < smallestDiff) {
            smallestDiff = diff;
            closestIndex = index;
          }
        });

        const selectedEl = this.$refs.items?.[closestIndex];
        if (!selectedEl) return;

        // Show 4 items above the target time when possible
        const itemHeight = selectedEl.clientHeight;
        const scrollPosition = Math.max(0, selectedEl.offsetTop - itemHeight * 4);
        listEl.scrollTop = scrollPosition;
      });
    },

    parseTimeToMinutes(timeStr) {
      try {
        if (!timeStr) return 0;

        const dt = this.parseTimeInput(timeStr);
        return dt ? dt.hour * 60 + dt.minute : 0;
      } catch (error) {
        console.error("Error parsing time to minutes:", error);
        return 0;
      }
    },

    handleInput(value) {
      this.displayValue = value;
    },

    formatInputTime() {
      if (!this.displayValue) {
        this.$emit("input", "");
        this.$emit("update:datetime", null);
        return;
      }

      // Special handling for 3-4 digit inputs
      if (/^\d{3,4}$/.test(this.displayValue)) {
        const hour = parseInt(this.displayValue.length === 3 ? this.displayValue[0] : this.displayValue.slice(0, 2));
        const minute = parseInt(
          this.displayValue.length === 3 ? this.displayValue.slice(1) : this.displayValue.slice(2),
        );

        if (!isNaN(hour) && !isNaN(minute) && minute < 60) {
          let adjustedHour = hour;
          let isPM = false;
          if (this.matchValue) {
            const startTime = this.parseTimeInput(this.matchValue);
            if (startTime) {
              const startMinutes = startTime.hour * 60 + startTime.minute;
              const endMinutes = hour * 60 + minute;

              // If end time would be before start time, make it PM
              if (endMinutes < startMinutes) {
                isPM = true;
                adjustedHour = hour === 12 ? 12 : hour;
              } else {
                isPM = startTime.hour >= 12;
                adjustedHour = hour === 12 ? 12 : hour;
              }
            }
          } else {
            isPM = hour === 12 || (hour > 0 && hour < 7);
          }

          // Adjust hour for PM
          if (isPM && hour !== 12) {
            adjustedHour += 12;
          } else if (!isPM && hour === 12) {
            adjustedHour = 0;
          }

          const newTime = DateTime.fromObject(
            { hour: adjustedHour, minute, second: 0 },
            { zone: this.settings?.timeZone || "local" },
          );

          const formattedTime = this.formatTimeDisplay(newTime);
          this.displayValue = formattedTime;
          this.$emit("input", formattedTime);
          this.$emit("update:datetime", newTime.toUTC().toISO());
          return;
        }
      }

      const parsedTime = this.parseTimeInput(this.displayValue);
      if (!parsedTime) {
        this.displayValue = this.value ? this.formatTimeDisplay(this.value) : "";
        return;
      }

      const formattedTime = this.formatTimeDisplay(parsedTime);
      const utcTime = parsedTime.toUTC().toISO();
      this.displayValue = formattedTime;
      this.$emit("input", formattedTime);
      this.$emit("update:datetime", utcTime);
    },

    selectTime(time) {
      // When selecting from dropdown, keep the original format
      this.displayValue = time;

      // For 24-hour format, emit the time directly if it's already in correct format
      if (this.is24Hour && /^\d{2}:\d{2}:\d{2}$/.test(time)) {
        const [hours, minutes] = time.split(":");
        const dt = DateTime.fromObject(
          { hour: parseInt(hours), minute: parseInt(minutes), second: 0 },
          { zone: this.settings?.timeZone || "local" },
        );
        this.$emit("input", time);
        this.$emit("update:datetime", dt.toUTC().toISO());
      } else {
        const parsedTime = this.parseTimeInput(time);
        if (parsedTime) {
          const formattedTime = this.formatTimeDisplay(parsedTime);
          this.$emit("input", formattedTime);
          this.$emit("update:datetime", parsedTime.toUTC().toISO());
        }
      }
      this.closeDropdown();
    },

    openDropdown() {
      this.isOpen = true;
    },
    closeDropdown() {
      this.isOpen = false;
    },
    handleFocus() {
      this.rawInput = this.displayValue;
    },
    handleBlur() {
      this.closeDropdown();
      this.formatInputTime();
      this.showError = true;
    },
    isMatchingTime(time) {
      if (!this.matchValue) return false;
      return this.normalizeTime(time) === this.normalizeTime(this.matchValue);
    },
    normalizeTime(time) {
      if (!time) return "";
      if (!time.includes("M")) return time;

      const [timePart, meridiem] = time.split(" ");
      const [hours, minutes] = timePart.split(":");
      let hour = parseInt(hours, 10);

      if (meridiem === "PM" && hour !== 12) hour += 12;
      if (meridiem === "AM" && hour === 12) hour = 0;

      return `${hour.toString().padStart(2, "0")}:${minutes}`;
    },
    handleClickOutside(event) {
      const dropdown = this.$refs.dropdown;
      const input = this.$refs.input.$el;

      if (dropdown && !dropdown.contains(event.target) && !input.contains(event.target)) {
        this.closeDropdown();
      }
    },
  },
  watch: {
    value: {
      immediate: true,
      handler(newVal) {
        if (newVal) {
          this.displayValue = this.formatTimeDisplay(newVal);
        }
      },
    },
    times: {
      immediate: true,
      handler(newTimes) {
        console.log("Times prop received:", newTimes);
      },
    },
    isOpen(newVal) {
      if (newVal) {
        console.log("Dropdown opened, times:", this.times);
        this.$nextTick(() => {
          this.scrollToSelected();
          document.addEventListener("mousedown", this.handleClickOutside);
        });
      } else {
        document.removeEventListener("mousedown", this.handleClickOutside);
      }
    },
  },
  beforeDestroy() {
    document.removeEventListener("mousedown", this.handleClickOutside);
  },
};
</script>

<style lang="scss">
.time-select {
  position: relative;
  width: 100%;

  &__dropdown {
    position: absolute;
    top: 100%;
    left: 0;
    right: 0;
    z-index: 1000;
    background: var(--v-background-base, #ffffff);
    border-radius: 4px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
    margin-top: 2px;

    &.theme--dark {
      background: var(--v-background-base, #1e1e1e);
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
    }
  }

  &__list {
    overflow-y: auto;
    max-height: 300px;
    scrollbar-width: thin;
    &::-webkit-scrollbar {
      width: 6px;
    }
    &::-webkit-scrollbar-thumb {
      background-color: var(--v-primary-lighten4, rgba(0, 0, 0, 0.2));
      border-radius: 3px;
    }
    .theme--dark & {
      &::-webkit-scrollbar-thumb {
        background-color: var(--v-primary-darken4, rgba(255, 255, 255, 0.2));
      }
    }
  }

  &__item {
    padding: 8px 16px;
    cursor: pointer;
    transition: background-color 0.2s;
    height: 40px;
    display: flex;
    align-items: center;
    user-select: none;
    color: var(--v-text-base, rgba(0, 0, 0, 0.87));

    &:hover {
      background-color: var(--v-primary-lighten5, rgba(0, 0, 0, 0.04));
    }

    &--selected {
      color: var(--v-primary-base, #1976d2) !important;

      &:hover {
        background-color: var(--v-primary-darken1);
      }
    }

    &--matching {
      font-weight: 500;

      &.theme--dark {
        color: var(--v-primary-lighten2);
      }

      &.time-select__item--selected {
        background-color: var(--v-primary-base);
        color: white;
      }
    }

    &.theme--dark {
      color: var(--v-text-base, rgba(255, 255, 255, 0.87));

      &:hover:not(.time-select__item--selected) {
        background-color: var(--v-primary-darken4, rgba(255, 255, 255, 0.04));
      }
    }
  }
}
</style>
